import React, { useState, useEffect } from 'react';
import { getSubscribers, deleteSubscriber, updateSubscriber, verifySubscriberEmail } from '../../services/api';
import { formatDate } from '../../utils/formatters';
import { trackAdminAction } from '../../utils/analytics';
import Button from '../common/Button';
import Modal from '../common/Modal';
import { debug } from '../../utils/debug';

interface Subscriber {
  id: string;
  tokenId: string; // Link to the public token
  email: string;
  subscriptionDate: any;
  status: string;
  verified: boolean;
  language: string;
  metadata: {
    source: string;
    referrer: string;
    userAgent: string;
    country: string;
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
  };
  lastContactDate?: any;
}

const SubscriberTable: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedSubscriber, setSelectedSubscriber] = useState<Subscriber | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterVerified, setFilterVerified] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const loadSubscribers = async (reset = false) => {
    try {
      setLoading(true);
      
      const filterOptions: any = {};
      if (filterStatus !== 'all') {
        filterOptions.status = filterStatus;
      }
      
      if (filterVerified !== 'all') {
        filterOptions.verified = filterVerified === 'verified';
      }
      
      debug.log('Loading subscribers with filters', filterOptions);
      
      const result = await getSubscribers(10, reset ? null : lastDoc, filterOptions);
      
      if (reset) {
        setSubscribers(result.subscribers as Subscriber[]);
      } else {
        setSubscribers(prev => [...prev, ...(result.subscribers as Subscriber[])]);
      }
      
      setLastDoc(result.lastDoc);
      setHasMore(result.subscribers.length === 10);
      setError(null);
    } catch (err) {
      console.error('Error loading subscribers:', err);
      debug.error('Error loading subscribers:', err);
      setError('Failed to load subscribers. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadSubscribers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterVerified]);
  
  const handleLoadMore = () => {
    loadSubscribers();
  };
  
  const handleDeleteClick = (subscriber: Subscriber) => {
    setSelectedSubscriber(subscriber);
    setIsDeleteModalOpen(true);
  };
  
  const handleViewClick = (subscriber: Subscriber) => {
    setSelectedSubscriber(subscriber);
    setIsViewModalOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedSubscriber) return;
    
    try {
      await deleteSubscriber(selectedSubscriber.id);
      setSubscribers(prev => prev.filter(s => s.id !== selectedSubscriber.id));
      trackAdminAction('delete_subscriber', { email: selectedSubscriber.email });
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error('Error deleting subscriber:', err);
      setError('Failed to delete subscriber. Please try again.');
    }
  };
  
  const handleStatusChange = async (subscriberId: string, newStatus: string) => {
    try {
      await updateSubscriber(subscriberId, { status: newStatus });
      setSubscribers(prev => 
        prev.map(s => s.id === subscriberId ? { ...s, status: newStatus } : s)
      );
      trackAdminAction('update_subscriber_status', { id: subscriberId, status: newStatus });
    } catch (err) {
      console.error('Error updating subscriber status:', err);
      debug.error('Error updating subscriber status:', err);
      setError('Failed to update subscriber status. Please try again.');
    }
  };
  
  const handleVerifyEmail = async (subscriberId: string) => {
    try {
      debug.log('Verifying subscriber email', { subscriberId });
      await verifySubscriberEmail(subscriberId);
      setSubscribers(prev => 
        prev.map(s => s.id === subscriberId ? { ...s, verified: true } : s)
      );
      trackAdminAction('verify_subscriber_email', { id: subscriberId });
      setError(null);
    } catch (err) {
      console.error('Error verifying subscriber email:', err);
      debug.error('Error verifying subscriber email:', err);
      setError('Failed to verify subscriber email. Please try again.');
    }
  };
  
  const handleExport = () => {
    // Create CSV content
    const headers = ['Email', 'Subscription Date', 'Status', 'Verified', 'Language'];
    const csvContent = [
      headers.join(','),
      ...subscribers.map(s => [
        s.email,
        formatDate(s.subscriptionDate),
        s.status,
        s.verified ? 'Yes' : 'No',
        s.language
      ].join(','))
    ].join('\\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    trackAdminAction('export_subscribers');
  };
  
  const filteredSubscribers = subscribers.filter(subscriber => 
    subscriber.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Status
            </label>
            <select
              id="statusFilter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="verifiedFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Verification
            </label>
            <select
              id="verifiedFilter"
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-1">
              Search by Email
            </label>
            <input
              type="text"
              id="searchTerm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <Button onClick={handleExport} variant="outline">
          Export CSV
        </Button>
      </div>
      
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
          {error}
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Verified
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Language
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSubscribers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  {loading ? 'Loading subscribers...' : 'No subscribers found'}
                </td>
              </tr>
            ) : (
              filteredSubscribers.map((subscriber) => (
                <tr key={subscriber.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {subscriber.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {formatDate(subscriber.subscriptionDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={subscriber.status}
                      onChange={(e) => handleStatusChange(subscriber.id, e.target.value)}
                      className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="active">Active</option>
                      <option value="unsubscribed">Unsubscribed</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        subscriber.verified 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {subscriber.verified ? 'Verified' : 'Unverified'}
                      </span>
                      {!subscriber.verified && (
                        <button
                          onClick={() => handleVerifyEmail(subscriber.id)}
                          className="ml-2 text-xs text-blue-600 hover:text-blue-900"
                          title="Mark as verified"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {subscriber.language}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewClick(subscriber)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDeleteClick(subscriber)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {hasMore && (
        <div className="mt-4 text-center">
          <Button
            onClick={handleLoadMore}
            disabled={loading}
            variant="secondary"
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <div className="flex justify-end space-x-3">
            <Button
              onClick={() => setIsDeleteModalOpen(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="danger"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>
          Are you sure you want to delete the subscriber with email:{' '}
          <strong>{selectedSubscriber?.email}</strong>?
        </p>
        <p className="mt-2 text-gray-600">
          This action cannot be undone.
        </p>
      </Modal>
      
      {/* View Subscriber Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Subscriber Details"
        size="lg"
      >
        {selectedSubscriber && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Basic Information</h3>
              <div className="mt-2 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p>{selectedSubscriber.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Subscription Date</p>
                  <p>{formatDate(selectedSubscriber.subscriptionDate)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p>{selectedSubscriber.status}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Verified</p>
                  <p>{selectedSubscriber.verified ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Language</p>
                  <p>{selectedSubscriber.language}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Contact</p>
                  <p>{selectedSubscriber.lastContactDate ? formatDate(selectedSubscriber.lastContactDate) : 'Never'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Token ID</p>
                  <p className="break-all">{selectedSubscriber.tokenId || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Document ID</p>
                  <p className="break-all">{selectedSubscriber.id}</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium">Metadata</h3>
              <div className="mt-2 space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-500">Source</p>
                  <p className="break-all">{selectedSubscriber.metadata?.source || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Referrer</p>
                  <p className="break-all">{selectedSubscriber.metadata?.referrer || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Country</p>
                  <p>{selectedSubscriber.metadata?.country || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">UTM Parameters</p>
                  <p>
                    Source: {selectedSubscriber.metadata?.utm_source || 'N/A'}<br />
                    Medium: {selectedSubscriber.metadata?.utm_medium || 'N/A'}<br />
                    Campaign: {selectedSubscriber.metadata?.utm_campaign || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">User Agent</p>
                  <p className="break-all text-xs">{selectedSubscriber.metadata?.userAgent || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriberTable;